import {
  DataCellValue,
  DataColumn,
  DataRow,
  ExportFormat,
  ExportOptions,
  GenericWarning,
  IdentifierColumn,
  KpiWithChangeColumn,
} from '../../types';
import { GroupInfo, LeaderboardInfo, LeaderColumn } from './data';
import { AvailableTemplate, ColumnType, DataCell, DataTable, ExcelExportDto } from '@ecosystem/export-api';
import { getExportContext, mapValueToDataCell } from '../../export';
import { TableColumn } from '@ecosystem/export-api/dist/dto/data-table';
import { getIdentifiers } from '../../data/helpers';
import { Formatting } from '../../data/formatting';
import { STICKY_EXCEL_ROW_COUNT } from './constants';
import { getGlobalWarningMessages } from '../utils';

function getEmptyValuesArray(length: number): (number | null)[] {
  return Array(length).fill(null);
}

export const chartDataToExportData = (
  exportFormat: ExportFormat,
  { fileHeaderSettings, fileName, contentName }: ExportOptions,
  info: LeaderboardInfo,
  groupInfo: GroupInfo[] = [],
  currency: string | undefined,
  rowIdField: string,
  globalWarnings: GenericWarning[] = []
): ExcelExportDto => {
  const leaderboardInfo = removeGroupSummariesFromLeaderboardInfo(info, groupInfo, rowIdField);
  switch (exportFormat) {
    case ExportFormat.Xlsx:
      return {
        template: AvailableTemplate.Excel,
        name: fileName,
        worksheets: [
          {
            context: [...getExportContext(fileHeaderSettings), ...getGlobalWarningMessages(globalWarnings)],
            worksheetName: contentName,
            data: createWorksheetData(leaderboardInfo, groupInfo, currency),
            frozen: { ySplit: STICKY_EXCEL_ROW_COUNT },
          },
        ],
      };

    default:
      throw new Error('Export format is not supported');
  }
};

const removeGroupSummariesFromLeaderboardInfo = (info: LeaderboardInfo, groupInfo: GroupInfo[], rowIdField: string) => {
  const copyLeaderboardInfoData = [...info.rowsData];
  const groupNames = groupInfo.map(group => group.name);
  return {
    ...info,
    rowsData: copyLeaderboardInfoData.filter(data => !groupNames.includes(data.values[rowIdField] as string)),
  };
};

function createHeaderRow(info: LeaderboardInfo): TableColumn[] {
  return info.columns.reduce((acc, current) => {
    const mainHeaderColumns = current.column;

    const totalNumberOfColumns = getTotalNumberColumns(current);

    for (let i = 0; i < totalNumberOfColumns; i++) {
      acc.push({ header: mainHeaderColumns.label, type: ColumnType.Text, style: { width: 17 } });
    }
    return acc;
  }, []);
}

function createAllMarketRow(info: LeaderboardInfo): DataCell[] {
  const row: DataCell[] = [{ value: 'Market', type: ColumnType.Text }];
  const totalsData = info.totalsData[0].values;
  const restData = info.columns.slice(1);

  return restData.reduce((acc, current) => {
    if (!current.column.arrayLength) {
      acc.push({ value: '-', type: ColumnType.Text });
      return acc;
    }

    const value = totalsData[current.column.name];

    if (!value) {
      const totalNumberOfColumns = getTotalNumberColumns(current);
      for (let i = 0; i < totalNumberOfColumns; i++) {
        acc.push({ value: '-', type: ColumnType.Text });
      }
      return acc;
    }

    const mainColumnValues = Object.entries(value).map(([_key, data]) =>
      mapValueToDataCell(data, current.column.formatHint, current.column.dataType)
    );
    acc.push(...mainColumnValues);

    const changeValues = getChangeValuesForColumn(totalsData, current.column);
    acc.push(...changeValues);
    return acc;
  }, row);
}

const getTotalNumberColumns = (current: LeaderColumn) => {
  const mainHeaderColumns = current.column;
  const subHeaderColumns = current.subLabels;
  const totalNumberOfSubColumns = subHeaderColumns?.length - 1;
  const totalNumberOfColumns = Number.isInteger(mainHeaderColumns.arrayLength)
    ? mainHeaderColumns.arrayLength + totalNumberOfSubColumns
    : 1;
  return totalNumberOfColumns;
};

function getChangeValuesForColumn(data: Record<string, DataCellValue>, currentColumn: DataColumn) {
  const columnName = (currentColumn as KpiWithChangeColumn).changeName;
  const columnValues = data[columnName] || getEmptyValuesArray(currentColumn.arrayLength);
  const changeValues = Object.entries(columnValues).map(([_key, data]) =>
    mapValueToDataCell(data, (currentColumn as KpiWithChangeColumn).changeFormatHint, currentColumn.dataType)
  );

  // we do not need first column as it is empty values
  const [_firstColumn, ...restColumns] = changeValues;

  return restColumns;
}

const mergeGroupSummaryWithProducts = (groupData: GroupInfo[]) => {
  // gather all groups with their products
  const groupSummary = groupData
    .map(groupInfo => {
      const groupProducts = groupInfo?.data;
      const groupSummary = groupInfo.groupSummary;

      return [getEmptyRow(groupSummary), groupSummary, ...groupProducts];
    })
    .flat();
  return groupSummary;
};

const getEmptyRow = (referenceRow: DataRow): DataRow => {
  return {
    values: {
      ...Object.entries(referenceRow.values).reduce((acc, [key, value]) => {
        acc[key] = Array.isArray(value) ? ['', ''] : '';
        return acc;
      }, {}),
    },
    hasData: false,
  };
};

const createSummaryForGroups = (info: LeaderboardInfo, groupData: GroupInfo[]) => {
  const groupSummary = mergeGroupSummaryWithProducts(groupData);

  const withInfo = {
    ...info,
    rowsData: groupSummary,
  };
  return createDataRows(withInfo);
};

function createDataRows(info: LeaderboardInfo): DataCell[][] {
  return info.rowsData.map(row => {
    const columnValues: DataCell[] = [];

    const identifierColumn = info.columns[0].column as IdentifierColumn;
    const identifierName = getIdentifiers(identifierColumn, row, true).name;
    columnValues.push(mapValueToDataCell(identifierName, null, null));

    const nonIdColumns = info.columns.slice(1);

    const columnsWithIdentifier = nonIdColumns.filter(column => {
      const col = column.column as IdentifierColumn;
      return col.identifier?.length > 0;
    });

    columnsWithIdentifier?.forEach(column => {
      const col = column.column as IdentifierColumn;
      const identifierValue = getIdentifiers(col, row, true).name;
      columnValues.push(mapValueToDataCell(identifierValue, null, null));
    });

    const columnsWithoutIdentifier = nonIdColumns.filter(column => {
      const col = column.column as IdentifierColumn;
      return col.identifier?.length === undefined || col.identifier?.length === 0;
    });

    return columnsWithoutIdentifier.reduce((acc, current) => {
      if (!current.column.arrayLength) {
        acc.push(
          mapValueToDataCell(
            row.values[current.column.name] as string | number,
            current.column.formatHint,
            current.column.dataType
          )
        );
        return acc;
      }

      const value = row.values[current.column.name] || getEmptyValuesArray(current.subLabels.length);

      const values = Object.entries(value).map(([_key, data]) =>
        mapValueToDataCell(data, current.column.formatHint, current.column.dataType)
      );
      acc.push(...values);

      const changeValues = getChangeValuesForColumn(row.values, current.column);
      acc.push(...changeValues);
      return acc;
    }, columnValues);
  });
}

function createPeriodsRow(info: LeaderboardInfo): DataCell[] {
  return info.columns.reduce((acc, current) => {
    if (!current.column.arrayLength) {
      acc.push({
        value: '',
        type: ColumnType.Text,
      });
      return acc;
    }

    const allMainColumnLabels =
      current.subLabels.map(label => ({ value: label.toString(), type: ColumnType.Text })) ?? [];
    acc.push(...allMainColumnLabels);

    const [firstPeriod, ...restPeriods] = allMainColumnLabels;
    const allChangeColumnLabels = restPeriods.map((period, index) => {
      const changeLabel = index === 0 ? firstPeriod.value : restPeriods[index - 1].value;
      return { ...period, value: `Change - ${changeLabel} vs ${period.value}` };
    }, []);

    acc.push(...allChangeColumnLabels);
    return acc;
  }, []);
}
export const createWorksheetData = (info: LeaderboardInfo, groupInfo: GroupInfo[], currency: string): DataTable => {
  return {
    currencySymbol: ' ' + (Formatting.getCurrencySymbol(currency) || currency) + ' ',
    dataColumns: createHeaderRow(info),
    summaryRows: [createPeriodsRow(info), createAllMarketRow(info)].concat(createSummaryForGroups(info, groupInfo)),
    dataRows: createDataRows(info),
  };
};
